import React, { memo, useEffect, useRef, useState } from 'react';
import { createUseStyles, Loader } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import styles from './styles/loadingOverlay.styles';
const useStyles = createUseStyles(styles, 'LoadingOverlay');

const DEFAULT_EXTRA_COLUMNS = 3;

const getMeasuredOverlayHeight = (id, element) => {
    if (id) {
        const baseElement = document.getElementById(id);
        if (baseElement) {
            const overlay = baseElement.querySelector('.ag-overlay');
            const gridRoot = baseElement.querySelector('.ag-root');
            const rootWrapper = baseElement.querySelector('.ag-root-wrapper');
            const height =
                overlay?.clientHeight ||
                gridRoot?.clientHeight ||
                rootWrapper?.clientHeight ||
                baseElement.clientHeight ||
                0;

            if (height > 0) {
                return height;
            }
        }
    }

    if (!element) {
        return 0;
    }

    let current = element;

    while (current) {
        const height = current.clientHeight || current.offsetHeight;

        if (height > 0) {
            return height;
        }

        current = current.parentElement;
    }

    return element.getBoundingClientRect().height || 0;
};

const SkeletonRow = ({ classes, avatars, loaderColumns, rowHeight }) => {
    const extraColumnCount = loaderColumns ? Math.max(0, loaderColumns - 1) : DEFAULT_EXTRA_COLUMNS;

    return (
        <div
            className={classes.skeletonRow}
            data-testid="skeleton-row"
            style={{ height: rowHeight }}
        >
            {avatars ? (
                <div className={classes.skeletonAvatar} data-testid="skeleton-avatar" />
            ) : null}
            <div
                className={classnames(classes.skeletonBar, classes.skeletonBarPrimary)}
                data-testid="skeleton-bar"
            />
            <div className={classes.skeletonBars}>
                {Array.from({ length: extraColumnCount }).map((_, index) => (
                    <div
                        key={`col-${index}`}
                        className={classnames(classes.skeletonBar, classes.skeletonBarSecondary)}
                    />
                ))}
                <div className={classnames(classes.skeletonBar, classes.skeletonBarFlex)} />
            </div>
        </div>
    );
};

const LoadingOverlay = memo(
    ({
        id,
        useLoaderPlaceholder,
        loaderRowHeight,
        loaderRows = 11,
        loaderColumns,
        useLoaderFullHeight,
        placeholderOptions = { avatars: true },
        gridContext,
    }) => {
        const classes = useStyles();
        const overlayRef = useRef(null);
        const [overlayHeight, setOverlayHeight] = useState(0);
        const { avatars } = placeholderOptions;
        const rowHeight = loaderRowHeight || 50;
        const showSkeletonPlaceholder = useLoaderPlaceholder && !gridContext.current?.startRow;

        useEffect(() => {
            if (!showSkeletonPlaceholder || !useLoaderFullHeight) {
                return undefined;
            }

            const update = () => setOverlayHeight(getMeasuredOverlayHeight(id, overlayRef.current));

            update();

            const targets = [id ? document.getElementById(id) : null, overlayRef.current].filter(
                Boolean,
            );

            if (!targets.length) {
                return undefined;
            }

            const observer = new ResizeObserver(update);
            targets.forEach((target) => observer.observe(target));

            return () => observer.disconnect();
        }, [id, showSkeletonPlaceholder, useLoaderFullHeight, rowHeight]);

        const skeletonRowCount =
            useLoaderFullHeight && overlayHeight > 0
                ? Math.ceil(overlayHeight / rowHeight)
                : loaderRows;

        if (showSkeletonPlaceholder) {
            return (
                <div
                    ref={overlayRef}
                    className={classes.overlay}
                    data-testid="loading-overlay-shell"
                >
                    <div className={classes.skeletonBody}>
                        {Array.from({ length: skeletonRowCount }).map((_, index) => (
                            <SkeletonRow
                                key={`row-${index}`}
                                classes={classes}
                                avatars={avatars}
                                loaderColumns={loaderColumns}
                                rowHeight={rowHeight}
                            />
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <div className={classes.root}>
                <Loader size="massive" />
            </div>
        );
    },
);

export default LoadingOverlay;
